import { useEffect, useState } from 'react';
import { Wallet, Banknote, GitCompare } from 'lucide-react';
import { cur } from '@/lib/currency';
import api from '@/lib/api';

/**
 * Shared Financial Summary Header for report pages.
 *
 * Reuses the existing GET /dashboard/stats endpoint (no new backend) and
 * surfaces three headline balances: Cash in Hand, Bank Balance and
 * Inter-branch Net. Pass `branchId` to scope the figures to one branch.
 */
const CARDS = [
  { key: 'cash_in_hand', label: 'Cash in Hand', icon: Wallet, bg: 'bg-success/10 border-success/30', iconBg: 'bg-success/15 text-success', tone: 'text-success' },
  { key: 'bank_in_hand', label: 'Bank Balance', icon: Banknote, bg: 'bg-info/10 border-info/30', iconBg: 'bg-info/15 text-info', tone: 'text-info' },
  { key: 'inter_branch_net', label: 'Inter-branch Net', icon: GitCompare, bg: 'bg-violet-50 border-violet-200 dark:bg-violet-950/20 dark:border-violet-900', iconBg: 'bg-violet-100 text-violet-600 dark:bg-violet-900/40 dark:text-violet-300', tone: 'text-violet-600 dark:text-violet-400' },
];

export const FinancialSummaryHeader = ({ branchId = '', className = '' }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get('/dashboard/stats', {
          params: { branch_ids: branchId || undefined },
        });
        if (alive) setStats(data);
      } catch {
        if (alive) setStats(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [branchId]);

  return (
    <div className={`grid grid-cols-1 sm:grid-cols-3 gap-2 ${className}`} data-testid="financial-summary-header">
      {CARDS.map(({ key, label, icon: Icon, bg, iconBg, tone }) => {
        const val = Number(stats?.[key] || 0);
        return (
          <div key={key} className={`rounded-xl border p-2 flex items-center gap-2.5 ${bg}`} data-testid={`fsh-${key}`}>
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${iconBg}`}>
              <Icon size={16} />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground truncate">{label}</p>
              <p className={`text-base font-bold tabular-nums truncate ${loading ? 'text-muted-foreground animate-pulse' : (val < 0 ? 'text-error' : tone)}`} data-testid={`fsh-${key}-value`}>
                {loading ? '…' : `${cur()} ${val.toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FinancialSummaryHeader;
